import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import type { ComponentProps } from 'react';

export const DropdownMenu = RadixDropdown.Root;
export const DropdownMenuTrigger = RadixDropdown.Trigger;
export const DropdownMenuRadioGroup = RadixDropdown.RadioGroup;
export const DropdownMenuSeparator = () => <RadixDropdown.Separator className="bg-border my-1 h-px" />;
export const DropdownMenuLabel = ({ className, ...p }: ComponentProps<typeof RadixDropdown.Label>) => (
  <RadixDropdown.Label
    className={cn('text-fg-subtle px-2 py-1.5 text-xs font-semibold tracking-wide uppercase', className)}
    {...p}
  />
);

export function DropdownMenuContent({ className, ...props }: ComponentProps<typeof RadixDropdown.Content>) {
  return (
    <RadixDropdown.Portal>
      <RadixDropdown.Content
        sideOffset={6}
        className={cn(
          'border-border bg-bg-elevated text-fg z-50 min-w-[12rem] rounded-md border p-1 text-sm shadow-[var(--shadow)]',
          className,
        )}
        {...props}
      />
    </RadixDropdown.Portal>
  );
}

export function DropdownMenuItem({ className, ...props }: ComponentProps<typeof RadixDropdown.Item>) {
  return (
    <RadixDropdown.Item
      className={cn(
        'data-[highlighted]:bg-bg-hover flex cursor-default items-center gap-2 rounded px-2 py-1.5 outline-none select-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof RadixDropdown.CheckboxItem>) {
  return (
    <RadixDropdown.CheckboxItem
      className={cn(
        'data-[highlighted]:bg-bg-hover flex cursor-default items-center gap-2 rounded px-2 py-1.5 outline-none select-none',
        className,
      )}
      {...props}
    >
      <span className="flex h-4 w-4 items-center justify-center">
        <RadixDropdown.ItemIndicator>✓</RadixDropdown.ItemIndicator>
      </span>
      {children}
    </RadixDropdown.CheckboxItem>
  );
}

/**
 * Yatay dizilebilen seçenek (tema gibi üç şıklı ayarlar için).
 *
 * Düz bir <button> kullanılsaydı menü içinde odak alamazdı: Radix menüde kendi roving
 * tabindex'ini uyguluyor ve Tab menüyü kapatıyor — yani ayar yalnızca fareyle
 * değiştirilebilirdi. RadioItem olarak yazılınca ok tuşlarıyla da geziliyor.
 */
export function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: ComponentProps<typeof RadixDropdown.RadioItem>) {
  return (
    <RadixDropdown.RadioItem
      className={cn(
        'flex flex-1 cursor-default items-center justify-center gap-1.5 rounded px-2 py-1 text-xs outline-none select-none',
        'text-fg-muted data-[highlighted]:bg-bg-hover data-[state=checked]:bg-brand-600 data-[state=checked]:text-white',
        className,
      )}
      {...props}
    >
      {children}
    </RadixDropdown.RadioItem>
  );
}

/** Alt menü (ör. araç çubuğunun "…" menüsünde işlem grupları). */
export const DropdownMenuSub = RadixDropdown.Sub;

export function DropdownMenuSubTrigger({
  className,
  children,
  ...props
}: ComponentProps<typeof RadixDropdown.SubTrigger>) {
  return (
    <RadixDropdown.SubTrigger
      className={cn(
        'data-[highlighted]:bg-bg-hover data-[state=open]:bg-bg-hover flex cursor-default items-center gap-2 rounded px-2 py-1.5 outline-none select-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="text-fg-muted ml-auto h-3.5 w-3.5" />
    </RadixDropdown.SubTrigger>
  );
}

export function DropdownMenuSubContent({
  className,
  ...props
}: ComponentProps<typeof RadixDropdown.SubContent>) {
  return (
    <RadixDropdown.Portal>
      <RadixDropdown.SubContent
        sideOffset={4}
        className={cn(
          'border-border bg-bg-elevated text-fg z-50 max-h-[70vh] min-w-[12rem] overflow-y-auto rounded-md border p-1 text-sm shadow-[var(--shadow)]',
          className,
        )}
        {...props}
      />
    </RadixDropdown.Portal>
  );
}
